import { inject, injectable } from '@wix/typesafe-inversify';
import type {
  IPanelsApi,
  IPanelsApiFactory,
  OpenDashboardOptions,
} from '@wix/blocks-widget-services/panels';

import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';
import { parseBlocksPanelAction } from '../../utils/parseBlocksPanelAction/parseBlocksPanelAction';
import type { ITranslations } from '../Translations/ITranslations';
import type { IPanelHandlers } from './IPanelHandlers';
import {
  DashboardActionType,
  parseBlocksDashboardAction,
} from '../../utils/parseBlocksDashboardAction';
import type { IReadyAppData } from '../ReadyAppData/IReadyAppData';
import { Services } from '../../ioc/services';
import { NotificationType } from '@wix/editor-platform-sdk-types';
import _ from 'lodash';
import type { IPlatformEssentials } from '../../types/PlatformTypes';
import { monitored } from '../Monitoring/monitored';
import type { IPromiseVoider } from '../PromiseVoider/IPromiseVoider';
import { waitForTime } from '../../utils/waitForTime';
import type { IExperiments } from '../Experiments/IExperiments';
import { ExperimentNames } from '../Experiments/ExperimentNames';

@injectable()
export class PanelHandlers implements IPanelHandlers {
  private panelsApiPromise: Promise<IPanelsApi> | undefined;

  constructor(
    @inject(Services.EditorSDK)
    private editorSDK: EditorSDK,
    @inject(Services.Translations)
    private translations: ITranslations,
    @inject(Services.PanelsApiFactory)
    private panelsApiFactory: IPanelsApiFactory,
    @inject(Services.ReadyAppData)
    private readyAppDataService: IReadyAppData,
    @inject(Services.PlatformEssentials)
    private readonly platformEssentials: IPlatformEssentials,
    @inject(Services.PromiseVoider)
    private readonly PromiseVoider: IPromiseVoider,
    @inject(Services.Experiments)
    private readonly experiments: IExperiments,
  ) {}

  public async initialize() {
    await this.PromiseVoider.executeAndVoid(() => this.initPanelsApi());
  }

  @monitored('open-user-panel')
  public async tryOpenUserPanel(componentRef: ComponentRef, id: string) {
    const panelAction = parseBlocksPanelAction(id);
    if (panelAction) {
      await this.openBlocksPanel(panelAction.panelName, componentRef);
    }

    if (await this.tryOpenDashboardForPageId(id)) {
      return;
    }

    await this.tryOpenDashboardForDevComponent(id);
  }

  @monitored('handle-open-add-elements-panel')
  public async openAddElementsPanel(componentRef: ComponentRef) {
    await this.executePanelsApiAction(async (panelsApi) => {
      await panelsApi.openAddElementsPanel(componentRef);
    });
  }

  private async tryOpenDashboardForPageId(actionId: string): Promise<boolean> {
    const action = parseBlocksDashboardAction(
      actionId,
      DashboardActionType.OpenDashboard,
    );

    if (!action) {
      return false;
    }

    const dashboardId = action.dashboardId;
    await this.openBlocksDashboard(
      dashboardId ? { pageId: dashboardId } : undefined,
    );

    return true;
  }

  private async tryOpenDashboardForDevComponent(
    actionId: string,
  ): Promise<boolean> {
    const action = parseBlocksDashboardAction(
      actionId,
      DashboardActionType.OpenDashboardByDevCenterCompId,
    );
    const dashboardId = action?.dashboardId;

    if (!dashboardId) {
      return false;
    }

    const params = { devCenterComponentId: dashboardId };
    await this.openBlocksDashboard(params);

    return true;
  }

  private initPanelsApi() {
    this.panelsApiPromise = this.panelsApiFactory.createPanelsApi({
      editorSDK: this.editorSDK,
      editorType: this.readyAppDataService.editorType,
      essentials: this.platformEssentials,
      usePlatformizedAppDescriptor: this.experiments.enabled(
        ExperimentNames.UsePlatformizedAppDescriptor,
      ),
    });

    return this.panelsApiPromise;
  }

  @monitored('handle-open-panel')
  private async openBlocksPanel(panelName: string, componentRef: ComponentRef) {
    await this.executePanelsApiAction(async (panelsApi) => {
      await panelsApi.openBlocksPanel(panelName, componentRef);
    });
  }

  @monitored('handle-open-dashboard')
  private async openBlocksDashboard(options?: OpenDashboardOptions) {
    await this.executePanelsApiAction(async (panelsApi) => {
      // openDashboardPanel is resolved only when the dashboard is closed.
      // We don't want to wait for it,
      // but still want to ensure that method is executed and catch error and report transaciton
      await Promise.race([
        panelsApi.openDashboardPanel(options),
        waitForTime(500), // timeout for api to reject  open dashboard panel in case of error
      ]);
    });
  }

  private async executePanelsApiAction(
    action: (panelsAPI: IPanelsApi) => Promise<void>,
  ) {
    try {
      if (!this.panelsApiPromise) {
        this.panelsApiPromise = this.initPanelsApi();
      }
      const panelsApi = await this.panelsApiPromise;
      await action(panelsApi);
    } catch (ex) {
      const retry = await this.showError();
      if (retry) {
        this.panelsApiPromise = undefined;
        await this.executePanelsApiAction(action);
      } else {
        throw ex;
      }
    }
  }

  private async showError(): Promise<boolean> {
    const t = this.translations.translate.bind(this.translations);
    const isLinkClicked =
      await this.editorSDK.editor.showUserActionNotification('', {
        type: NotificationType.Error,
        title: t('Universal_Error_Widget_Panel_Header'),
        message: t('Universal_Error_Widget_Panel_Text'),
        link: {
          caption: t('Universal_Error_Widget_Panel_Link'),
          onClick: _.noop,
        },
      });
    return Boolean(isLinkClicked);
  }
}
